import * as logger from "../utils/logger";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { convertMinutesToTimeString, getLatestRuleSet } from "../utils/helpers";
import User from "../model/User";
import DutyStatusLog, { IAnnotation } from "../model/DutyStatusLog";
import { nativeSpeak } from "../utils/native/nativeSpeak";
import { AppType, getAppType, isAppInBackground } from "../utils/native/nativeHelpers";
import { getNotificationController } from "../utils/native/notificationController";
import { dstCalculator, getStartOfDayUserTimezone } from "../utils/date";
import Notifications from "../utils/notifications";
import UserHosRuleSet from "../model/UserHosRuleSet";
import UserHosRuleSets, { UserHosRuleSetService } from "../model/UserHosRuleSets";
import { getServerDate } from "../utils/storage/date";
import { isDriverViewingInspectionMode, isDriverOnAnyWorkflow } from "../utils/uri";
import idSerializer from "geotab-uuid";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import Api from "./api";
import { isNoVehicle } from "../utils/device";
import { UserService } from "../utils/users";
import { extend, htmlEscape } from "../utils/vanilla";
import TranslationService from "./translation";
import Service from "./base";
import { getAvailabilityDialog, getDrivingInViolationDialog, getBreakInterruptionDialog,
        TViolationValidationResult, RestBreakViolationValidationResult, RestBreakAlert,
        CanadianMinimumRestBreakViolationValidationResult, SplitSbViolationValidationResult } from "../utils/violations";
import {
    EngineCalculator, HOURS, type IHosAvailability, type IValidation, MINUTES,
    convertDecimalToTimeString, type IUserRuleSetValidationResult, type IDutyStatusLog,
    isInCanada,
    hasCustomStartOfDay,
    isStartOfDayAllowed,
    getEffectiveRuleSets,
    getCurrentCycle,
    timeSpanToMs,
    getRawRuleSet,
    getLatestWorkday,
    isRulesetCycleResetRequired,
    getLogRetentionDays as collectLogRetentionDays,
    getDefaultAvailability as collectDefaultAvailability,
    type THosStatus,
    type TViolationType,
    type IHosRuleset,
    ICustomCodeState,
    type TDiscreteAvailabilitySummary
} from "hos-tools";
import { applyDriverActionNecessary, Dialog } from "../ui/framework/dialog";
import { DriveStore } from "../store/reducers";
import { MobileActions } from "../store/mobile/actions";
import dayjs from "../utils/dayjs";
import { InOrNearViolationValidationResult } from "../utils/drivingViolations";
import { SecurityService } from "../utils/security";
import { DutyStatusLogService } from "../model/DutyStatusLogs";
import { DataActions } from "../store/data/actions";
import { IAlertsAvailability } from "../store/data/types";
import { IFeatureFlagsService } from "./featureFlags";
import { FeatureFlagName } from "../utils/featureFlagName";
import { AVAILABILITY_KEYS_TO_IGNORE, normalizeDiscreteAvailability, normalizeAggregateAvailability, computeAvailabilityAlerts } from "../utils/availabilityAlerts";
import { HosRuleResultsActions } from "../store/hosRuleResults/actions";

const VIOLATION_STATUSES: THosStatus[] = ["D", "Work", "WorkExemption"];

export const availabilityNamesDictionary: IIdDictionary<string> = {
    cycle: "{0}-hour cycle on-duty limit",
    cycleDriving: "{0}-hour cycle driving limit",
    rest: "Required break after {0}-hour driving time",
    driving: "{0}-hour driving limit",
    duty: "{0}-hour on-duty limit",
    workday: "{0}-hour workday limit",
    dailyDuty: "{0}-hour on-duty per day limit",
    dailyOff: "{0}-hour off-duty per day minimum",
    dailyDriving: "{0}-hour driving per day limit",
    cycleRest: "Driving without {0} period(s) of {1}-hour consecutive off-duty in the preceding {2} days.",
    dutySinceCycleRest: "{0} hour(s) on-duty since {1}-hour consecutive off",
    dailyRest: "{0}-hour daily rest requirement"
};

export const availabilityNamesDictionaryCanada: IIdDictionary<string> = {
    cycle: "{0}-hour cycle on-duty limit",
    cycleDriving: "{0}-hour cycle driving limit",
    rest: "Required break after {0}-hour driving time",
    driving: "{0}-hour driving limit",
    duty: "{0}-hour on-duty limit",
    workday: "{0}-hour workday limit",
    dailyDuty: "{0}-hour on-duty per {1} day(s) limit",
    dailyOff: "{0}-hour off-duty per {1} day(s) minimum ({2} continuous hours of rest on Day 2)",
    dailyDriving: "{0}-hour driving per {1} day(s) limit",
    cycleRest: "Driving without {0} period(s) of {1}-hour consecutive off-duty in the preceding {2} days",
    dutySinceCycleRest: "{0} hour(s) on-duty since {1}-hour consecutive off",
    dailyRest: "{0}-hour daily rest requirement"
};

const getAvailabilityDictValue = (key: string, isDayTwo = false) => {
    return isDayTwo ? availabilityNamesDictionaryCanada[key] : availabilityNamesDictionary[key];
};

/** HosProcessor writes SituationalDrivingClear exactly 10ms before the resulting automatic D/ON. */
const SDC_TO_AUTOMATIC_STATUS_OFFSET_MS = 10;
const SDC_ENDED_PC_RECOVERY_WINDOW_MS = 24 * HOURS;

export interface IHosProcessResult extends IValidation {
    inViolation: boolean;
}

export default class HosProcess extends Service {
    private readonly api: Api;
    private readonly translationService: TranslationService;
    private readonly store: DriveStore;
    private readonly rulesEngine: EngineCalculator;
    private availabilityDialog: Dialog | undefined;
    private lastHosStatusType: THosStatus | undefined;
    private ruleProcessResult: IHosProcessResult | undefined;
    private lastDispatchedMissedCycleLogId: string | undefined;
    private lastDispatchedSdcEndedPcLogId: string | undefined;

    constructor(
        api: Api,
        translationService: TranslationService,
        private readonly securityService: SecurityService,
        private readonly userService: UserService,
        private readonly dutyStatusLogService: DutyStatusLogService,
        store: DriveStore,
        private readonly userHosRuleSetService: UserHosRuleSetService,
        private readonly featureFlagsService: IFeatureFlagsService
    ) {
        super();
        this.api = api;
        this.rulesEngine = new EngineCalculator(dstCalculator);
        this.translationService = translationService;
        this.store = store;
    }

    getUserHosRuleSets() {
        let rulesets = new UserHosRuleSets(this.store);
        rulesets.fetch(this.api);

        return rulesets;
    }

    getLatestHosRuleSet() {
        return this.getUserHosRuleSets().getLatest();
    }

    getLatestHosRuleSetIdShortened(useNone = false) {
        return this.userHosRuleSetService.getLatestHosRuleSetIdShortened(useNone);
    }

    isLatestRulesetCarrierExemption(): boolean {
        return this.userHosRuleSetService.isLatestRulesetCarrierExemption();
    }

    isNoneCanadaRuleSet(rulesetId: string) {
        return rulesetId.toLowerCase().indexOf("nonecanada") > -1;
    }

    isAlbertaRuleSet(rulesetId: string) {
        return rulesetId.toLowerCase().indexOf("alberta") > -1;
    }

    getLatestCanadianRulesetWithCycle(): UserHosRuleSet | undefined {
        return this.getLatestUserHosRuleSetMatching(rulesetId => isInCanada(rulesetId) && !this.isNoneCanadaRuleSet(rulesetId) && !this.isAlbertaRuleSet(rulesetId));
    }

    /** The driver's most recent ruleset record whose Start of Day the server persists (see hos-tools `isStartOfDayAllowed`). */
    getLatestRulesetWithAllowedStartOfDay(): UserHosRuleSet | undefined {
        return this.getLatestUserHosRuleSetMatching(isStartOfDayAllowed);
    }

    formatStartOfDay(userRuleSet: UserHosRuleSet | undefined) {
        if (!userRuleSet || !userRuleSet.startOfDay || !hasCustomStartOfDay(userRuleSet.hosRuleSet.id)) {
            return undefined;
        }

        let splitTimeSpan = userRuleSet.startOfDay.split(":");
        let hours = parseInt(splitTimeSpan[0]);
        let minutes = parseInt(splitTimeSpan[1]);

        return dayjs(new Date(1, 1, 1, hours, minutes, 0, 0)).format("hh:mm A");
    }

    getStandardStatusLogs(user: IIdEntity): DutyStatusLog[] {
        return this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, user.id).filter(hosLog => hosLog.isStandardStatusDriverLog()));
    }

    getLatestLogForUser(user: IIdEntity): DutyStatusLog | null {
        let logCollection = this.getStandardStatusLogs(user);
        return this.dutyStatusLogService.getLatestLog(logCollection);
    }

    getLatestLoginLogForUser(user: DeepReadonly<IUser> | null): DutyStatusLog | undefined {
        let logCollection = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, user?.id).filter(hosLog => hosLog.isLoginLogoffLog()));
        return logCollection[logCollection.length - 1];
    }

    checkPCLimitExceededNew(apiUserId: string | undefined): Promise<void> {
        let pcLimitWarningMessage = this.store.getState().mobile.pcLimitWarningMessage;
        let reachedDailyPCDrivingLimit = this.dutyStatusLogService.reachedDailyPCDrivingLimit(apiUserId);
        let reachedLimit = reachedDailyPCDrivingLimit.reachedLimit;
        let latestPCLog = reachedDailyPCDrivingLimit.latestPCLog;
        if (reachedLimit) {
                let isPCWarningDismissed = false;
                const annotationsHash = apiUserId && this.store.getState().hosAnnotations[apiUserId] || {};
                if (latestPCLog?.annotations) {
                    isPCWarningDismissed = latestPCLog.annotations.some(ann => {
                        if (ann.id) {
                            let comment: string | null = null;
                            if (!ann.comment) {
                                let annotation = annotationsHash[ann.id];
                                comment = annotation ? annotation.comment : null;
                            } else {
                                comment = ann.comment;
                            }
                            if (comment) {
                                return comment.indexOf("Personal Conveyance exemption limit warning was dismissed by the driver.") > -1;
                            }
                        }
                        return false;
                    });
                }
                if (!isPCWarningDismissed && !pcLimitWarningMessage) {
                    let isCanadianDriver = isInCanada(this.getLatestHosRuleSetIdShortened());
                    let pcWarningMessage = isCanadianDriver ? "You have surpassed your Personal Conveyance exemption limit." :
                        "Personal Conveyance limit exceeded. Please remove exemption.";
                    if (isAppInBackground()) {
                        let currentUser = this.userService.getCurrentUser();
                        void this.translationService.getTranslator().translate(pcWarningMessage).then(translatedMsg => {
                            let language = this.translationService.getLanguage();
                            logger.log(
                                logger.Source.DEBUG,
                                `nativeSpeak: Triggered notification for PC exemption limit in background: "${ translatedMsg }" (AppType: ${ AppType[getAppType()] })`,
                                currentUser
                            );
                            nativeSpeak(translatedMsg, null, language);
                            getNotificationController().nativeNotify(translatedMsg)
                                .catch((error) => {
                                    logger.error(logger.Source.NATIVE, `nativeNotify: Failed to notify PC exemption limit in background: ${ error?.message || error }`, currentUser);
                                });
                        });
                    }
                    this.store.dispatch(MobileActions.setPcLimitWarningMessage(pcWarningMessage));
                } else if (isPCWarningDismissed && pcLimitWarningMessage !== undefined) {
                    this.store.dispatch(MobileActions.setPcLimitWarningMessage(undefined));
                }
        } else if (pcLimitWarningMessage) {
            this.store.dispatch(MobileActions.setPcLimitWarningMessage(undefined));
        }
        return Promise.resolve();
    }

    process(isSilent = false, afterSync = false, getAllRecaps = false) {
        let apiUser = this.api.getUser();
        let apiUserId = apiUser ? apiUser.id : undefined;

        if (!apiUser || !apiUserId || !(this.securityService.can("useHos", apiUserId) || (this.securityService.can("attemptHos", apiUserId) && isNoVehicle()))) {
            // If no apiUser, or no HOS access, don't process rules.
            // Prevents alerts from appearing all together
            return undefined;
        }

        let logCollection = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, apiUserId).filter(hosLog => hosLog.isHosProcessLog()));
        if (!logCollection || logCollection.length === 0) {
            // If no user or logs, skip the rules processing
            return undefined;
        }

        let mostRecentLog = this.dutyStatusLogService.getLatestLog(logCollection);
        let ruleSets: UserHosRuleSet[] = this.getUserHosRuleSets().toArray();
        let determineIfInViolation = (result: IHosProcessResult) => {
            if (!mostRecentLog || VIOLATION_STATUSES.indexOf(mostRecentLog.status) === -1) {
                return false;
            }

            let violations = result.violations;
            return violations.some(violation => !violation.toDate);
        };

        // Double check that the clock isn't out of sync
        let currentTime = getServerDate();
        if (mostRecentLog) {

            //if the latest log made earlier has a greater datetime than the system clock (current time), send a warning to the user
            if (this.store.getState().data.ELDJurisdiction !== "EWD" && currentTime < mostRecentLog.getDate()) {
                Notifications.add("clockOutOfSync", {
                    type: "app",
                    message: "Your current time is earlier than the time that the last log that was created. Your time is out of sync with the server",
                    critical: true,
                    navigateTo: "hos/tabs/logs/logs"
                }, apiUserId);
            } else {
                Notifications.remove("clockOutOfSync", apiUserId);
            }
        }

        this.ruleProcessResult = this.validate(logCollection, ruleSets, currentTime, apiUser.timeZoneId, getAllRecaps);

        this.ruleProcessResult.inViolation = determineIfInViolation(this.ruleProcessResult);

        this.store.dispatch(HosRuleResultsActions.setHosRuleResults({
            userId: apiUserId,
            ruleResult: this.ruleProcessResult,
            afterSync
        }));

        dispatcher.trigger(DispatcherEvents.HosRuleCompute, apiUserId, this.ruleProcessResult, afterSync);

        if (mostRecentLog) {
            this.lastHosStatusType = mostRecentLog.status;
        }

        if (!isSilent && !isDriverOnAnyWorkflow() && !isDriverViewingInspectionMode()) {
            void this.showAvailabilityAlerts(this.ruleProcessResult);
        }

        void this.checkPCLimitExceededNew(apiUserId);

        if (afterSync) {
            this.recoverMissedPcIgnitionCycle(apiUserId);
            this.recoverSdcEndedPc(apiUserId);
        }

        logger.log(logger.Source.SERVICES, "Processed HOS rules", apiUser);
        return this.ruleProcessResult;
    }

    getRuleResult(isSilent = false, forceReprocess = false, getAllRecaps = false) {
        if (!this.ruleProcessResult || forceReprocess) {
            return this.process(false, false, getAllRecaps);
        }

        if (!isSilent && !isDriverOnAnyWorkflow() && !isDriverViewingInspectionMode()) {
            void this.showAvailabilityAlerts(this.ruleProcessResult);
        }

        return this.ruleProcessResult;
    }

    getUnidentifiedDrivingRetentionStartDate (isCanada: boolean): Date {
        return this.getRetentionStartDate(undefined, undefined,  isCanada ? 14 : 7);
    }

    /**
     * Returns retention start date based on user's ruleSet and shifted accoarding to user's timezone
     * Retention period should be the number of retention days multiplied by `multiplier` param (EXCLUDING today)
     * (e.g. if retention days is 7 and multiplier is 1 then retention start date should be 7 days ago. Today=Feb 9th -> Retention=Feb 2nd)
     */
    getRetentionStartDate (multiplier = 1, initialDate?: Date, cycleDays?: number): Date {
        let serverDate = initialDate || getServerDate();
        let oneCycle = cycleDays ?? this.getLogRetentionDays(this.getLatestHosRuleSetIdShortened(true));

        // Substract retention period days * multiplier
        let startDate = new Date(serverDate.getTime());
        startDate.setUTCDate(startDate.getUTCDate() - (oneCycle * multiplier));

        // Get offsets difference between initial date (current date) and start date
        let startOffset = dstCalculator.getOffset(serverDate) - dstCalculator.getOffset(startDate);
        let localStartOffset = serverDate.getTimezoneOffset() - startDate.getTimezoneOffset();
        startDate.setUTCMinutes(startDate.getUTCMinutes() + startOffset - localStartOffset);
        return getStartOfDayUserTimezone(startDate);
    }

    canNavigateNextDay (reportDate: Date) {
        let today = getStartOfDayUserTimezone(getServerDate());
        return reportDate < today;
    }

    getRuleSets() {
        // If the store is initialized with rulesets then we grab it from there, otherwise we get the rulesets from helpers.
        let cachedRuleSets = this.recreateRuleSetsDict(this.store.getState().data.ruleSets);
        let useLegacyTexasShortHaulRulesets = this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_TEXAS_SHORT_HAUL_RULESET_CORRECTION);
        let ruleSets = cachedRuleSets && Object.keys(cachedRuleSets).length > 0
            ? cachedRuleSets
            : getEffectiveRuleSets(useLegacyTexasShortHaulRulesets);

        if (!useLegacyTexasShortHaulRulesets) {
            return ruleSets;
        }

        let effectiveRuleSets = getEffectiveRuleSets(true);
        return {
            ...ruleSets,
            AmericaTexasShortHaul: { ...ruleSets.AmericaTexasShortHaul, ...effectiveRuleSets.AmericaTexasShortHaul },
            AmericaTexasShortHaul8Day: { ...ruleSets.AmericaTexasShortHaul8Day, ...effectiveRuleSets.AmericaTexasShortHaul8Day }
        };
    }

    getIgnoredLogCountAndDurationInCurrentCycle(): { count: number; duration: string } | undefined {
        let logCount = 0;
        let logDuration = 0;
        let logCollection = this.dutyStatusLogService.getHOSLogs(true);
        if (!this.ruleProcessResult) {
            return undefined;
        }
        let currentCycleStartTime = getCurrentCycle(this.ruleProcessResult.cycleSummaries)?.dateTime;
        let ignoredLogs = this.dutyStatusLogService.getSortedHosLogs(logCollection.filter(hosLog => !!hosLog.isIgnored));
        let getMinuteDuration = (log: DutyStatusLog): number => this.dutyStatusLogService.getDuration(logCollection, log) / (1000 * 60);
        // If no user or logs or don't have clearance, skip
        if (!ignoredLogs || ignoredLogs.length === 0 || !this.securityService.can("canIgnoreHOSLogs")) {
            return undefined;
        }
        ignoredLogs.forEach((log) => {
            if (currentCycleStartTime && log.getDate() >= currentCycleStartTime) {
                logDuration += (getMinuteDuration(log) || 0);
                logCount += 1;
            }
        });
        let ignoredLogDurationString = convertMinutesToTimeString(logDuration);
        if (logCount > 0) {
            return {
                count: logCount,
                duration: ignoredLogDurationString
            };
        }
        return undefined;
    }

    getLogRetentionDays (ruleSet?: string) {
        let userRuleSet = ruleSet;
        if (!userRuleSet) {
            userRuleSet = this.getLatestHosRuleSetIdShortened(true);
        }
        return collectLogRetentionDays(this.getValidRuleSet(userRuleSet));
    }

    getAmountOfDays(currentUser: DeepReadonlyObject<IUser> | null, cycleMultiplier: number) {
        return this.getLogRetentionDays(currentUser ? this.getLatestHosRuleSetIdShortened() : "None") * cycleMultiplier + 1;
    }

    validate(logs: DutyStatusLog[], rulesets: UserHosRuleSet[], dummyLogDate: Date, timeZoneId: string | undefined, getAllRecaps = false): IHosProcessResult {
        let timeZone = timeZoneId;
        let customCodes: ICustomCodeState = {
            enableAutoCreateNoneDeferralLog: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_AUTO_CREATE_NONE_DEFERRAL_LOG),
            enablePreventSplitCompleteWhenViolationFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_PREVENT_SPLIT_COMPLETE_WHEN_VIOLATION_FIX),
            disableRemoveOrTrimViolationsAfter16hException: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_REMOVE_OR_TRIM_VIOLATIONS_AFTER_16H_EXEMPTION),
            disableUnfreezeSBSplitNotCompleteFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_UNFREEZE_SB_SPLIT_NOT_COMPLETE_FIX),
            disableExemptionLogsInteractionRefactor: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_EXEMPTION_LOGS_INTERACTION_REFACTOR),
            disableOngoingViolationRulesetFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_ONGOING_VIOLATION_RULESET_FIX),
            disableCycleRestartUsedInFollowingValidations: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_CYCLE_RESTART_USED_IN_FOLLOWING_VALIDATIONS),
            disablePrevCycleRestDurationCountingFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_PREV_CYCLE_REST_DURATION_COUNTING_FIX),
            disableDeferralRestSubtractionFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_DEFERRAL_REST_SUBTRACTION_FIX),
            disableErrRestReconciliationFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_ERR_REST_RECONCILIATION),
            disableDstDailyAvailabilityFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_DST_DAILY_AVAILABILITY_FIX),
            disableUsaPropertySplitPairingReuseFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_USA_PROPERTY_SPLIT_PAIRING_REUSE_FIX),
            disableUsaPropertyResetSplitPairingFix: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_USA_PROPERTY_RESET_SPLIT_PAIRING_FIX),
            enableUsaPreventWorkdayResetPairingForward: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_USA_PREVENT_WORKDAY_RESET_PAIRING_FORWARD),
            disableNonSplitAvailability: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_NON_SPLIT_AVAILABILITY),
            disableTexasShortHaulRulesetCorrection: this.featureFlagsService.hasFeatureFlag(
                FeatureFlagName.DISABLE_TEXAS_SHORT_HAUL_RULESET_CORRECTION
            ),
            disableCycleResetPeriod: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_CYCLE_RESET_PERIOD),
            enableEmergencyResponseExemption: this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_EMERGENCY_RESPONSE_EXEMPTION)
        };
        return this.rulesEngine.validate(
            logs.filter(log => log.state !== "Requested") as IDutyStatusLog[],
            rulesets,
            dummyLogDate.toISOString(),
            customCodes,
            timeZone,
            undefined,
            getAllRecaps,
            this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_DISCRETE_AVAILABILITY_FOR_UPCOMING_VIOLATIONS),
            this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_NEXT_AVAILABILITY_EVENT_DATE)
        ) as IHosProcessResult;
    }

    getRulesetHours(violationType: string, ruleset: string, processResult: IHosProcessResult) {
        let hours: string[] = [];
        let convertToHours = (timeString?: string) => timeString ? timeSpanToMs(timeString) / HOURS : 0;
        let convertToMinutes = (timeString?: string) => timeString ? timeSpanToMs(timeString) / MINUTES : 0;
        let addToHours = (limitHours: number) => {
            hours.push(String(limitHours));
        };

        let rule = this.getRuleSets()[ruleset];

        if (rule) {
            const dayTwo = this.isDayTwo(processResult);
            let defaultAvailability = collectDefaultAvailability(ruleset, dayTwo);
            const addOffDutyNeeded = () => {
                if (violationType === "dailyOff" && processResult.offDutyNeeded) {
                    //if DayTwo, need to include additional details
                    hours.push(convertMinutesToTimeString(convertToMinutes(processResult.offDutyNeeded)));
                }
            };

            const haveValuesPerTwoDays = ["dailyDuty", "dailyOff", "dailyDriving", "cycleRest"];
            const daysMultiplier = Number((!!haveValuesPerTwoDays.find(dv => dv === violationType) && dayTwo)) + 1;
            if (violationType === "dailyRest") {
                this.addDailyRestHours(rule, addToHours);
            } else if (violationType === "workday" || violationType === "driving") {
                this.addWorkdayOrDrivingHours(violationType, defaultAvailability, processResult, convertToHours, addToHours);
            } else if (violationType === "cycleRest" || violationType === "dutySinceCycleRest") {
                this.addCycleRestHours(violationType, rule, defaultAvailability, daysMultiplier, convertToHours, addToHours);
            } else {
                let availabilityHours = defaultAvailability[violationType as keyof IHosAvailability];
                if (availabilityHours) {
                    addToHours(convertToHours(availabilityHours));
                    addToHours(daysMultiplier);
                }
               addOffDutyNeeded();
            }
        }

        return hours;
    }

    addWorkdayOrDrivingHours(
        violationType: string,
        defaultAvailability: IHosAvailability,
        processResult: IHosProcessResult,
        convertToHours: (timeString?: string) => number,
        addToHours: (limitHours: number) => void
    ) {
        let currentWorkday = processResult && getLatestWorkday(processResult.workdaySummaries);
        if (violationType === "workday") {
            let workday = convertToHours(defaultAvailability.workday);
            if (workday && currentWorkday && currentWorkday.workdayExtension) {
                workday += convertToHours(currentWorkday.workdayExtension);
            }
            workday && addToHours(workday);
        } else {
            let driving = convertToHours(defaultAvailability.driving);
            if (driving && currentWorkday && currentWorkday.driveExtension) {
                driving += convertToHours(currentWorkday.driveExtension);
            }
            driving && addToHours(driving);
        }
    }

    addCycleRestHours(
        violationType: string,
        rule: DeepReadonlyObject<IHosRuleset>,
        defaultAvailability: IHosAvailability,
        daysMultiplier: number,
        convertToHours: (timeString?: string) => number,
        addToHours: (limitHours: number) => void
    ) {
        let cycleRestMinimum = ((rule.config.cycle && rule.config.cycle.cycleRestMinimum || 0) / HOURS) * daysMultiplier;
        if (violationType === "cycleRest") {
            let cycleRestDays = rule.config.cycle && rule.config.cycle.cycleRestDays || 0;
            let cycleRestPeriods = 1;
            if (rule.config.cycle?.cycleRestPeriods !== undefined && rule.config.cycle?.cycleRestPeriods !== 0) {
                cycleRestPeriods = rule.config.cycle.cycleRestPeriods;
            }
            addToHours(cycleRestPeriods); //cycleRest period(s)
            addToHours(cycleRestMinimum);
            addToHours(cycleRestDays);
        } else {
            let dutySinceCycleRestMaximum = convertToHours(defaultAvailability.dutySinceCycleRest);
            addToHours(dutySinceCycleRestMaximum);
            addToHours(cycleRestMinimum);
        }
    }

    addDailyRestHours(rule: DeepReadonlyObject<IHosRuleset>, addToHours: (limitHours: number) => void) {
        let dailyRestMinimum = rule.config.rest?.dailyRestMinimum;
        if (dailyRestMinimum) {
            addToHours(dailyRestMinimum / HOURS);
        }
    }

    replaceAllHoursInString(alertStrings: string[], alertHours: string[][]) {
        return alertStrings.map((alertString, index) => {
            let formattedAlertString = alertString;
            alertHours[index].forEach((rulesetHour, hoursIndex) => {
                formattedAlertString = formattedAlertString.replace(`{${ hoursIndex }}`, rulesetHour);
            });
            return formattedAlertString;
        });
    }

    createDrivingInViolationWarning(currentRuleSet: string, processResult: IHosProcessResult, okCallback: () => void, cancelCallback: (error: any) => void) {
        let violationTitle = "Changing your status to Driving will break the following rules:";
        let alerts: string[] = [];
        let alertHours: string[][] = [];

        const dayTwo = this.isDayTwo(processResult);
        for (let i = 0; i < processResult.violations.length; i++) {
            let violationType = this.violationTypeToAvailabilityProp(processResult.violations[i].type as TViolationType);
            let availabilityName = getAvailabilityDictValue(violationType, dayTwo);
            availabilityName && alerts.push(availabilityName);
            let hours = this.getRulesetHours(violationType, currentRuleSet, processResult);
            alertHours.push(hours);
        }

        void this.translationService.getTranslator().translate(alerts).then((alertStrings: string[]) => {
            let violationNameList = this.replaceAllHoursInString(alertStrings, alertHours);
            let alertDialog = getDrivingInViolationDialog(
                this.store,
                violationTitle,
                violationNameList,
                cancelCallback,
                okCallback,
                this.translationService.getTranslator()
            );
            return alertDialog.open()
                .then(applyDriverActionNecessary(this.store, window.webViewLayer.driverActionTypes.ConfirmHOSStatus));
        });
    }

    violationTypeToAvailabilityProp(violationType: TViolationType) {
        return violationType.charAt(0).toLowerCase() + violationType.slice(1);
    }

    showStatusChangeWarning(validationResults: TViolationValidationResult, okCallback: () => void, cancelCallback: (error: any) => void) {
        let question = "Would you still like to change your status?";
        let translator = this.translationService.getTranslator();
        let getValidationMessages = validationResults.map(validationResult => validationResult.getMessage(translator));

        void Promise.all(getValidationMessages).then(messages => {
            let alertDialog = getBreakInterruptionDialog(this.store, messages, question, cancelCallback, okCallback, this.translationService.getTranslator());
            return alertDialog.open()
                .then(applyDriverActionNecessary(this.store, window.webViewLayer.driverActionTypes.ConfirmHOSStatus));
        });
    }

    checkNewStatusForDrivingInViolation(newLogs: DutyStatusLog[]) {
        if (!newLogs.length || newLogs.some(log => log.origin === "Automatic") || !newLogs.some(log => log.status === "D")) {
            return Promise.resolve();
        }

        let apiUser = this.api.getUser();
        if (!apiUser) {
            return Promise.resolve();
        }

        let userRuleSets = this.getUserHosRuleSets();
        let rulesets = userRuleSets.toArray();
        let now = getServerDate();
        let currentUserRuleSet = this.getValidRuleSet(this.getLatestHosRuleSetIdShortened(true));
        let logs = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, newLogs[0].getDriverId()).filter(hosLog => hosLog.isStandardStatusDriverLog()));

        // If validating a range of logs (>1) don't need to check previous status
        let latestLog = newLogs.length > 1 ? null : this.dutyStatusLogService.getLatestLog(logs, newLogs[0], log => log.isNormalLog());

        // We don't need to check violations if status hasn't changed
        if (latestLog && latestLog.status === "D") {
            return Promise.resolve();
        }

        let resultsBeforeNewLogs = this.validate(logs, rulesets, now, apiUser.timeZoneId);
        const existingViolationKeys = new Set(
            resultsBeforeNewLogs.violations?.map(v => `${ v.type }-${ v.fromDate?.toISOString() || "" }`) ?? []
        );

        logs.push(...newLogs);
        let results = this.validate(logs, rulesets, now, apiUser.timeZoneId);

        const newViolations = results.violations?.filter(violation => {
            const violationKey = `${ violation.type }-${ violation.fromDate?.toISOString() || "" }`;
            return !existingViolationKeys.has(violationKey);
        }) ?? [];
        const currentViolations = results.violations?.filter(violation => {
            const end = violation.toDate;
            return !end || end.getTime() >= now.getTime();
        }) ?? [];

        return new Promise((res, rej) => {
            if (newViolations.length > 0 || currentViolations.length > 0) {
                const trimLogs = logs.map(log => ({
                    dateTime: log.dateTime,
                    status: log.status,
                    ...(log.deferralStatus && log.deferralStatus !== "None" && { deferralStatus: log.deferralStatus, deferralMinutes: log.deferralMinutes })
                }));
                const trimRulesets = rulesets.map(ruleset => ({ dateTime: ruleset.dateTime, id: ruleset.hosRuleSet.id, ...(ruleset.startOfDay && { startOfDay: ruleset.startOfDay })}));
                logger.log(logger.Source.DEBUG, "Driver received 'Driving in Violation' Warning: " + JSON.stringify({
                    logs: trimLogs,
                    rulesets: trimRulesets,
                    now
                }), apiUser);

                this.createDrivingInViolationWarning(currentUserRuleSet, results, res, rej);
            } else {
                res();
            }
        });
    }

    validateNewStatusForBreakingRest(newLog: DutyStatusLog): TViolationValidationResult {
        let userRuleSets = this.getUserHosRuleSets();
        let errors: TViolationValidationResult = [];
        let rulesets = userRuleSets.toArray();
        let logs = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false).filter(hosLog => hosLog.isStandardStatusDriverLog()));

        let latestLog = this.dutyStatusLogService.getLatestLog(logs, newLog, log => log.isNormalLog());
        let latestStatus = latestLog && latestLog.status;

        // If there is no latest log or latest status is not off duty status then proceed without warning
        if (!latestLog || !this.rulesEngine.isOffDutyStatus(latestStatus)) {
            return errors;
        }

        return this.addViolationForBreakingRest(newLog, errors, logs, rulesets);
    }

    getMinimumMaximumOffDutyDuration(results: IHosProcessResult, currentUserRuleSet: IHosRuleset) {
        let maxOffDutyDuration: number = currentUserRuleSet.config.workday ? currentUserRuleSet.config.workday.offDutyMinimum : 0;
        let minOffDutyDuration: number = maxOffDutyDuration - 2 * HOURS;
        if (this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_PREVENT_SPLIT_COMPLETE_WHEN_VIOLATION_FIX)) {
            minOffDutyDuration = 2 * HOURS;
            if (results.remainingSplitDuration !== undefined) {
                maxOffDutyDuration = results.remainingSplitDuration;
            }
        }
        return [minOffDutyDuration, maxOffDutyDuration];
    }

    addViolationForBreakingRest(newLog: DutyStatusLog, errors: TViolationValidationResult, logs: DutyStatusLog[], rulesets: UserHosRuleSet[]): TViolationValidationResult {
        let apiUser = this.api.getUser();
        if (!apiUser) {
            return errors;
        }
        let results = this.validate(logs, rulesets, getServerDate(), apiUser.timeZoneId);

        // If rest duration more then restBreakMinimum - 15 min and less then restBreakMinimum
        let currentCycle = results.cycleSummaries && results.cycleSummaries.length > 0 && results.cycleSummaries.sort((a, b) => a.dateTime > b.dateTime ? -1 : 1)[0];
        if (!currentCycle) {
            return errors;
        }
        let currentUserRuleSet: IHosRuleset = getRawRuleSet(this.getValidRuleSet(this.getLatestHosRuleSetIdShortened(true)));
        let minimumOffState: number = currentUserRuleSet.config.rest ? currentUserRuleSet.config.rest.restBreakMinimum : 0;
        let restDuration: number = currentCycle?.consecutiveRest ? timeSpanToMs(currentCycle.consecutiveRest) : 0;
        // if onDutyCountTowardRest is set to true and newLog is ON, don't create RestBreakViolation
        let isUsingOnDutyTowardRest = newLog.status === "ON" && (currentUserRuleSet && currentUserRuleSet.config.rest && currentUserRuleSet.config.rest.onDutyCountTowardRest);
        if (minimumOffState && restDuration > (minimumOffState - 15 * MINUTES) && restDuration < minimumOffState && !isUsingOnDutyTowardRest) {
            let restFinish = minimumOffState - restDuration;
            errors.push(new RestBreakViolationValidationResult(newLog, RestBreakAlert["OffBreak"], restFinish, minimumOffState));
        }

        // If rest duration more then minOffDutyState and before offDutyMinimum then show Off duty rest break alert
        let [minOffDutyState, maxOffDutyState] = this.getMinimumMaximumOffDutyDuration(results, currentUserRuleSet);
        if (maxOffDutyState && restDuration > minOffDutyState && restDuration < maxOffDutyState) {
            let showSplitMessage = currentUserRuleSet.config.splitConfig !== undefined;
            let restFinish = maxOffDutyState - restDuration;
            let requiredOffDuration = currentUserRuleSet.config.workday?.offDutyMinimum ?? maxOffDutyState;
            errors.push(new RestBreakViolationValidationResult(newLog, RestBreakAlert["OffDutyBreak"], restFinish, requiredOffDuration, showSplitMessage));
        }

        // If rest period is above 2 hours to ruleset cycle restart period
        let cycleRestart: number = currentUserRuleSet.config.cycle && currentUserRuleSet.config.cycle.cycleRestartMinimum || 0;
        if (cycleRestart && restDuration > cycleRestart - 2 * HOURS && restDuration < cycleRestart) {
            let restFinish = cycleRestart - restDuration;
            errors.push(new RestBreakViolationValidationResult(newLog, RestBreakAlert["cycleRestart"], restFinish, cycleRestart));
        }

        return errors;
    }

    validateNewStatusForCanadianRestDurationMinimum(newLog: DutyStatusLog) {
        const offDutyStatuses = ["OFF", "SB", "PC", "PC_Exempted", "WT"];
        let apiUser = this.api.getUser();
        if (!apiUser) {
            return [];
        }

        let logs = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getHOSLogs(false, newLog.getDriverId()).filter(log => log.isStandardStatusDriverLog()));
        let latestLog = this.dutyStatusLogService.getLatestLog(logs, newLog, log => log.isNormalLog());
        let latestStatus = latestLog && latestLog.status;

        let userRuleSets = this.getUserHosRuleSets();
        let rulesets = userRuleSets.toArray();
        let results = this.validate(logs, rulesets, getServerDate(), apiUser.timeZoneId);
        let currentCycle = results.cycleSummaries && results.cycleSummaries.length > 0 && results.cycleSummaries.sort((a, b) => a.dateTime > b.dateTime ? -1 : 1)[0];
        if (!currentCycle) {
            return [];
        }
        let duration: number = currentCycle?.consecutiveRest ? timeSpanToMs(currentCycle.consecutiveRest) : 0;
        let currentDay = results.daySummaries && results.daySummaries.length > 0 && results.daySummaries.sort((a, b) => a.dateTime > b.dateTime ? -1 : 1)[0];
        let isDeferring = currentDay && currentDay.deferralInfo;

        // Switching from an off-duty status (e.g. OFF) to non-off-duty (e.g. ON)
        if (offDutyStatuses.indexOf(newLog.status) === -1 && latestStatus && offDutyStatuses.indexOf(latestStatus) !== -1) {
            let currentUserRuleSet: IHosRuleset = getRawRuleSet(this.getValidRuleSet(this.getLatestHosRuleSetIdShortened(true)));
            let minRestDuration = currentUserRuleSet.config.rest && currentUserRuleSet.config.rest.restBreakMinimum || -1;

            if (duration < minRestDuration) {
                return [new CanadianMinimumRestBreakViolationValidationResult(minRestDuration, duration)];
            }
        }

        if (newLog.status !== "SB" && latestStatus === "SB" && !isDeferring) {
            let currentUserRuleSet: IHosRuleset = getRawRuleSet(this.getValidRuleSet(this.getLatestHosRuleSetIdShortened(true)));
            let minRestDuration = currentUserRuleSet.config.rest && currentUserRuleSet.config.rest.dailyRestMinimum || -1;

            if (duration < minRestDuration) {
                return [new SplitSbViolationValidationResult(duration, minRestDuration)];
            }
        }

        return [];
    }

    hasAvailability(ruleSetKey: string) {
        let availability = this.getDefaultAvailability(ruleSetKey);
        let availabilityKeys = Object.keys(availability);
        let isCarrierExempt = ruleSetKey === "CarrierExemption";
        return availabilityKeys.length > 0 && !(availabilityKeys.length === 1 && availabilityKeys[0] === "recap") && !isCarrierExempt;
    }

    getPreviewAvailabilityForNewRuleset(newRuleset: string, startOfDay?: string): IHosProcessResult | undefined {
        let currentUser = this.userService.getCurrentUser();
        if (!currentUser) {
            return undefined;
        }

        let logCollection = this.dutyStatusLogService.getHOSLogs(false).filter(hosLog => hosLog.isHosProcessLog());
        let userRuleSets = this.getUserHosRuleSets();
        userRuleSets.add(new UserHosRuleSet({
            id: "",
            hosRuleSet: { id: `HosRuleSet${ newRuleset }` },
            user: { id: currentUser.id },
            dateTime: getServerDate().toISOString(),
            startOfDay
        }));

        let rulesets = userRuleSets.toArray();
        let dummyLogDate = getServerDate();
        dummyLogDate.setSeconds(dummyLogDate.getSeconds() + 10);
        return this.validate(logCollection, rulesets, dummyLogDate, currentUser.timeZoneId);
    }

    getPreviewAvailabilityForNewIgnoredLog(newLog: DutyStatusLog): IHosProcessResult | undefined {
        let currentUser = this.userService.getCurrentUser();
        if (!currentUser) {
            return undefined;
        }

        let logCollection = this.dutyStatusLogService.getHOSLogs(false).filter(hosLog => hosLog.isHosProcessLog());

        let oldLog = logCollection.find(log => log.id === newLog.id);
        if (oldLog) {
            oldLog.isIgnored = newLog.isIgnored;
        }
        let userRuleSets = this.getUserHosRuleSets().toArray();

        let dummyLogDate = getServerDate();
        dummyLogDate.setSeconds(dummyLogDate.getSeconds() + 10);
        return this.validate(logCollection, userRuleSets, dummyLogDate, currentUser.timeZoneId);
    }

    /**
     * If ruleset exists in engine it will be returned. Otherwise return "None"
     */
    getValidRuleSet(ruleSetIndex: string): string {
        return this.isRuleSetValid(ruleSetIndex) ? ruleSetIndex : "None";
    }

    isRuleSetValid(ruleSetIndex: string) {
        return !!this.getRuleSets()[ruleSetIndex];
    }

    getRuleSet(ruleSet: string) {
        let ruleSets = this.getRuleSets();
        return this.isRuleSetValid(ruleSet) ? ruleSets[ruleSet] : ruleSets["None"];
    }

    getDriverRuleSet() {
        return this.getRuleSet(this.getLatestHosRuleSetIdShortened());
    }

    noAvailabilityBar(ruleset: DeepReadonlyObject<IHosRuleset>) {
        // If config is missing all of these, there is no availability to calculate. Hide the availability bar.
        let rulesetConfig = ruleset.config || {};
        return !(rulesetConfig.cycle && rulesetConfig.cycle.cycleMaximum) && !rulesetConfig.workday && !rulesetConfig.rest;
    }

    getDefaultAvailability(ruleset: string) {
        return collectDefaultAvailability(
            ruleset,
            false,
            this.featureFlagsService.hasFeatureFlag(FeatureFlagName.DISABLE_TEXAS_SHORT_HAUL_RULESET_CORRECTION)
        );
    }

    validateHosLog(hosLog: DutyStatusLog): Promise<TViolationValidationResult> {
        if (!hosLog.isNormalLog() || !hosLog.isStandardLog()) {
            return Promise.resolve([]);
        }

        const latestLog = this.dutyStatusLogService.getLatestStatusLog(hosLog.driver.id);
        const willNewLogBeLatestLog = !latestLog || hosLog.dateTime >= latestLog.dateTime;
        if (willNewLogBeLatestLog) {
            dispatcher.trigger(DispatcherEvents.AddDSL, hosLog);
        }
        return this.checkNewStatusForDrivingInViolation([hosLog])
            .then(() => willNewLogBeLatestLog ? this.getBreakInterruptViolationValidationResults(hosLog) : []);
    }

    getDriverHOSLogs(fromDate: Date, toDate: Date, user: IIdEntity, deviceIdList: string[] = [], statusFilter: string[] = [], originFilter: string[] = [], api: Api): Promise {
        if (deviceIdList.length === 0) {
            deviceIdList.push("ALL");
        }

        return Promise.all(deviceIdList.map((deviceId: string) => {
            return new Promise<any>((resolve, reject) => {
                let search = {
                    fromDate,
                    toDate,
                    statuses: statusFilter,
                    userSearch: user && { id: user.id },
                    deviceSearch: deviceId === "ALL" ? <any>null : {id: deviceId},
                    includeBoundaryLogs: true,
                    origin: originFilter.length > 0 ? originFilter : <any>null
                };

                api.call(
                    "Get",
                    {
                        typeName: "DutyStatusLog",
                        search
                    },
                    userLogCollection => {
                        resolve(userLogCollection);
                    },
                    errorObject => {
                        reject(errorObject);
                    }
                );
            });
        }))
        .then(logList => logList.reduce((currentLogs: any[], newLogs) => currentLogs.concat(newLogs), []));
    }

    getDriverChanges(fromDate: Date, toDate: Date, user: IIdEntity, deviceIds: string[], api: Api): Promise<any[]> {
        let deviceIdList = deviceIds;
        let unknownDeviceId = "NoDeviceId";
        let isNoDevice = (device: any) => device && (device === unknownDeviceId || (device.id && device.id === unknownDeviceId));
        let getUniqueDevices = function(logs: DutyStatusLog[], deviceIdsArr: any = []) {
            let deviceMap: IIdDictionary<string>  = {};
            let deviceList;

            // If given a list, we want to extend this list. Convert it back into an object.
            if (deviceIdsArr.length > 0) {
                deviceIdsArr.forEach((deviceId: string) => {
                    deviceMap[deviceId] = deviceId;
                });
            }

            logs.forEach((log) => {
                if (!deviceMap[log.device.id] && !isNoDevice(log.device)) {
                    deviceMap[log.device.id] = log.device.id;
                }
            });

            deviceList = Object.keys(deviceMap);
            return deviceList;
        };

        return new Promise<any[]>((resolve, reject) => {
            api.call(
                "Get",
                {
                    typeName: "DriverChange",
                    search: {
                        userSearch: { id: user.id },
                        fromDate,
                        toDate,
                        includeOverlappedChanges: true
                    }
                },
                newUserDriverChanges => {
                    let calls: any = [];

                    if (newUserDriverChanges.length > 0) {

                        // If we get user driver changes, check the vehicle on them and make sure they are also included in our list of device driver changes
                        deviceIdList = getUniqueDevices(newUserDriverChanges, deviceIdList);
                    }

                    // Get device driverchanges for each device
                    deviceIdList.forEach((deviceId: string) => {
                        calls.push([
                            "Get", {
                                typeName: "DriverChange",
                                search: {
                                    deviceSearch: { id: deviceId },
                                    fromDate,
                                    toDate,
                                    includeOverlappedChanges: true
                                }
                            }
                        ]);
                    });

                    api.multiCall(
                        calls,
                        allDeviceDriverChanges => {
                            resolve([newUserDriverChanges, allDeviceDriverChanges]);
                        },
                        errorObject => {
                            reject(errorObject);
                        }
                    );
                },
                errorObject => {
                    reject(errorObject);
                }
            );
        });
    }

    getNewAnnotation(comment: string, user: IIdEntity, dateTime?: string): IAnnotation {
        let annotationDateTime = dateTime || getServerDate().toISOString();

        return {
            id: idSerializer.get(),
            driver: { id: user.id },
            comment,
            dateTime: annotationDateTime
        };
    }

    canUseExemptions (currentUser: IUser, ruleset: DeepReadonlyObject<IHosRuleset>, ruleSetKey: string) {
        return ruleSetKey !== "CarrierExemption" || !!ruleset.config.bigDayBonus ||
        currentUser.isYardMoveEnabled || currentUser.isPersonalConveyanceEnabled ||
        currentUser.isAdverseDrivingEnabled || !!ruleset.config.oilwell;
    }

    getCommentWithDatetime(commentForOnLog: string, localizedDate: string, repairComments: string[] = []): string {
        if (repairComments.length === 0) {
            return `${ commentForOnLog } ${ localizedDate }`;
        }
        let dvirPartComment = "";
        for (let i = 0; i < repairComments.length; i++) {
            dvirPartComment += i < repairComments.length - 1 ? `${ repairComments[i] }, ` : `${ repairComments[i] }`;
        }
        return `${ commentForOnLog } [${ dvirPartComment }] ${ localizedDate }`;
    }

    getAnnotationForOnLog(comment: string) {
        if (comment === "PostTrip") {
            return "Asset Inspection: Post-trip";
        } else if (comment === "InTrip" || comment === "MidTrip") {
            return "Asset Inspection: In-trip";
        } else if (comment === "PreTrip") {
            return "Asset Inspection: Pre-trip";
        } else {
            return comment;
        }
    }

    shouldCreateOnLog (currentLog: DutyStatusLog | null | undefined, logCollection: DutyStatusLog[]): boolean {
        if (!currentLog) {
            return true;
        } else if (currentLog.status === "YM") {
            return false;
        } else if (currentLog.status === "PC_Exempted") {
            return false;
        } else if (currentLog.status === "ON") {
            let loginLog = this.dutyStatusLogService.getLatestLog(logCollection, undefined, (log: DutyStatusLog) => log.status === "Login");
            return loginLog && loginLog.getDate().getTime() > currentLog.getDate().getTime() ? true : false;
        } else {
            return true;
        }
    }

    validateUserHosRuleSet (newRuleSetId: string): IUserRuleSetValidationResult {
        let validationResult: IUserRuleSetValidationResult = {
            isValid: true
        };
        if (isRulesetCycleResetRequired(newRuleSetId)) {
            let hosValidationRes = this.getRuleResult();
            if (hosValidationRes && hosValidationRes.rulesetsThatCannotChange && hosValidationRes.rulesetsThatCannotChange.length > 0) {
                validationResult.isValid = false;
                validationResult.ruleSetToComplete = hosValidationRes.rulesetsThatCannotChange[0];
            }
        }
        return validationResult;
    }

    startSubscriptions() {
        this.stopSubscriptions();
        dispatcher.on(DispatcherEvents.SyncHos, () => this.process(false, true), this);
    }

    stopSubscriptions() {
        dispatcher.off(DispatcherEvents.SyncHos, undefined, this);
    }

    getBreakInterruptViolationValidationResults (hosLog: DutyStatusLog) {
        let currentUser = this.api.getUser();
        if (currentUser && isInCanada(this.getLatestHosRuleSetIdShortened())) {
            return this.validateNewStatusForCanadianRestDurationMinimum(hosLog);
        }

        switch (hosLog.status) {
            case "D":
            case "ON":
                return this.validateNewStatusForBreakingRest(hosLog);
            default:
                return [];
        }
    }

    checkIfBreakWillBeInterrupted() {
        let hosLog = new DutyStatusLog();
        hosLog.status = "ON";
        hosLog.dateTime = getServerDate().toISOString();

        return this.getBreakInterruptViolationValidationResults(hosLog);
    }

    checkIfInOrNearViolation(ruleResult?: IHosProcessResult, pendingRuleset?: string): InOrNearViolationValidationResult[] {
        if (!ruleResult) {
            return [];
        }

        if (!ruleResult || this.getLatestHosRuleSetIdShortened(true) === "None") {
            return [];
        }

        const minimumTimeToViolation = 2;
        let alerts: string[] = [];
        let alertHours: string[][] = [];
        let alertDurations: number[] = [];
        let hosRuleSet = pendingRuleset || this.getLatestHosRuleSetIdShortened();

        const dayTwo = this.isDayTwo(ruleResult);
        const flagEnabled = this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_DISCRETE_AVAILABILITY_FOR_UPCOMING_VIOLATIONS);
        const useDiscreteAvailability = flagEnabled && ruleResult.discreteAvailability;

        if (useDiscreteAvailability) {
            for (const pascalKey of Object.keys(ruleResult.discreteAvailability as TDiscreteAvailabilitySummary)) {
                const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
                const timeRemaining = (ruleResult.discreteAvailability as TDiscreteAvailabilitySummary)[pascalKey as keyof TDiscreteAvailabilitySummary];
                if (timeRemaining === undefined) {
                    continue;
                }
                if (timeRemaining / HOURS < minimumTimeToViolation) {
                    const availabilityName = getAvailabilityDictValue(camelKey, dayTwo);
                    if (!availabilityName) {
                        continue;
                    }
                    const hours = this.getRulesetHours(camelKey, hosRuleSet, ruleResult);
                    alertDurations.push(timeRemaining);
                    alerts.push(availabilityName);
                    alertHours.push(hours);
                }
            }
        } else {
            for (let key in ruleResult.availability) {
                if (AVAILABILITY_KEYS_TO_IGNORE.indexOf(key) > -1) {
                    continue;
                }
                if (!flagEnabled && key === "dailyDuty") {
                    continue;
                }

                let timeRemaining = timeSpanToMs(ruleResult.availability[key as keyof IHosAvailability]);
                if (timeRemaining / HOURS < minimumTimeToViolation) {
                    let availabilityName = getAvailabilityDictValue(key, dayTwo);
                    if (!availabilityName) {
                        continue;
                    }
                    let hours = this.getRulesetHours(key, hosRuleSet, ruleResult);
                    alertDurations.push(timeRemaining);
                    alerts.push(availabilityName);
                    alertHours.push(hours);
                }
            }
        }

        let nearViolationErrors: InOrNearViolationValidationResult[] = [];
        if (alerts.length > 0) {
            alerts.forEach((alert, index) => {
                nearViolationErrors.push(new InOrNearViolationValidationResult(alert, alertHours[index], alertDurations[index]));
            });
        }
        return nearViolationErrors;
    }

    destroy() {}

    private recoverMissedPcIgnitionCycle(apiUserId: string | undefined): void {
        if (!apiUserId) {
            return;
        }

        const hosLogs = this.dutyStatusLogService.getHOSLogs(true, apiUserId);
        const latestStandardLog = this.dutyStatusLogService.getLatestLog(hosLogs);
        if (!latestStandardLog?.isPCLog()) {
            this.lastDispatchedMissedCycleLogId = undefined;
            return;
        }

        const systemHosLogs = this.dutyStatusLogService.getSortedHosLogs(this.dutyStatusLogService.getSystemHOSLogs());
        const latestEngineLog = this.dutyStatusLogService.getLatestEngineLog(systemHosLogs);
        if (latestEngineLog?.status !== "EnginePowerupPC") {
            return;
        }

        const latestPcLog = this.dutyStatusLogService.getLatestPcLog(hosLogs);
        // PC_Exempted continues across ignition cycles — no prompt to recover.
        if (!latestPcLog || latestPcLog.status === "PC_Exempted") {
            return;
        }

        const pcStartTime = latestPcLog.getDate().getTime();
        const powerupTime = latestEngineLog.getDate().getTime();
        const hasCycleAfterPc = systemHosLogs.some(log => {
            const logTime = log.getDate().getTime();
            return log.status === "EngineShutdownPC" && logTime > pcStartTime && logTime < powerupTime;
        });
        if (!hasCycleAfterPc) {
            return;
        }

        if (this.lastDispatchedMissedCycleLogId === latestEngineLog.id) {
            return;
        }
        this.lastDispatchedMissedCycleLogId = latestEngineLog.id;

        logger.log(logger.Source.SERVICES, "recoverMissedPcIgnitionCycle: dispatching VehicleEndedStatus for missed PC ignition cycle", latestEngineLog);
        dispatcher.trigger(DispatcherEvents.VehicleEndedStatus, latestEngineLog);
    }

    private recoverSdcEndedPc(apiUserId: string | undefined): void {
        if (!apiUserId || this.store.getState().data.driving !== false) {
            return;
        }

        const hosLogs = this.dutyStatusLogService.getHOSLogs(true, apiUserId);
        const latestStandardLog = this.dutyStatusLogService.getLatestLog(hosLogs);
        if (latestStandardLog?.status !== "D" || latestStandardLog.origin !== "Automatic") {
            return;
        }

        const dLogTime = latestStandardLog.getDate().getTime();
        if (getServerDate().getTime() - dLogTime > SDC_ENDED_PC_RECOVERY_WINDOW_MS) {
            return;
        }

        const pairedSdc = this.dutyStatusLogService.getSystemHOSLogs().find(log =>
            log.isSituationalDrivingClear()
            && log.origin === "Automatic"
            && !log.isRemoved()
            && log.getDeviceId() === latestStandardLog.getDeviceId()
            && dLogTime - log.getDate().getTime() === SDC_TO_AUTOMATIC_STATUS_OFFSET_MS);
        if (!pairedSdc) {
            return;
        }

        const logBeforeD = this.dutyStatusLogService.getLatestLog(hosLogs, latestStandardLog);
        if (!logBeforeD?.isPCLog()) {
            return;
        }

        const latestPcLog = this.dutyStatusLogService.getLatestPcLog(hosLogs);
        if (latestPcLog?.status !== "PC") {
            return;
        }

        const handledLogScope = `${ this.store.getState().data.lastDatabase }:${ apiUserId }`;
        if (this.lastDispatchedSdcEndedPcLogId === latestStandardLog.id
            || this.store.getState().data.handledSdcEndedPcLogIds[handledLogScope] === latestStandardLog.id) {
            return;
        }
        logger.log(logger.Source.SERVICES, "recoverSdcEndedPc: dispatching VehicleEndedStatus for device-ended PC", latestStandardLog);
        const markSdcEndedPcPresented = () => {
            this.lastDispatchedSdcEndedPcLogId = latestStandardLog.id;
        };
        const markSdcEndedPcHandled = () => {
            markSdcEndedPcPresented();
            this.store.dispatch(DataActions.setHandledSdcEndedPcLogId({ [handledLogScope]: latestStandardLog.id }));
        };
        const markSdcEndedPcDismissed = () => {
            if (this.store.getState().data.handledSdcEndedPcLogIds[handledLogScope] !== latestStandardLog.id) {
                this.lastDispatchedSdcEndedPcLogId = undefined;
            }
        };
        dispatcher.trigger(DispatcherEvents.VehicleEndedStatus, latestStandardLog, {
            sdcEndedPc: true,
            onPromptPresented: markSdcEndedPcPresented,
            onPromptHandled: markSdcEndedPcHandled,
            onPromptDismissed: markSdcEndedPcDismissed
        });
    }

    private getUserFullName(): string {
        let user = new User(this.api.getUser());
        return user.getFullName();
    }

    private isDayTwo(processResult: IHosProcessResult): boolean {
        if (processResult.daySummaries?.length === 0) {
            return false;
        }
        return processResult.daySummaries?.sort((d1, d2) => d2.dateTime.getTime() - d1.dateTime.getTime())[0].deferralInfo?.status === "DayTwo";
    }

    private showAvailabilityAlerts(ruleResult: IHosProcessResult): Promise<void> {
        const apiUser = this.api.getUser();
        const userId = apiUser?.id;
        if (!userId) {
            return Promise.reject();
        }

        if (this.getLatestHosRuleSetIdShortened(true) === "None" || this.store.getState().data.ELDJurisdiction === "EWD") {
            return Promise.resolve();
        }

        const alerts: string[] = [];
        const alertHours: string[][] = [];
        const alertDurations: string[] = [];
        const availabilityAlerts: IIdDictionary<IAlertsAvailability> = extend(true, {}, this.store.getState().data.alertsAvailability);

        const hosRuleSet = this.getLatestHosRuleSetIdShortened();
        const dayTwo = this.isDayTwo(ruleResult);
        const flagEnabled = this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_DISCRETE_AVAILABILITY_FOR_UPCOMING_VIOLATIONS);
        const useDiscreteAvailability = flagEnabled && ruleResult.discreteAvailability;

        const normalized = useDiscreteAvailability
            ? normalizeDiscreteAvailability(ruleResult.discreteAvailability as TDiscreteAvailabilitySummary)
            : normalizeAggregateAvailability(ruleResult.availability, flagEnabled ? undefined : ["dailyDuty"]);

        const { triggeredAlerts, updatedAlerts, shouldRemoveDialog } = computeAvailabilityAlerts(
            normalized,
            availabilityAlerts[userId] || {}
        );

        availabilityAlerts[userId] = updatedAlerts;

        if (shouldRemoveDialog) {
            this.availabilityDialog?.remove();
        }

        for (const alert of triggeredAlerts) {
            const availabilityName = getAvailabilityDictValue(alert.key, dayTwo);
            if (!availabilityName) {
                continue;
            }
            const hours = this.getRulesetHours(alert.key, hosRuleSet, ruleResult);
            alertDurations.push(convertDecimalToTimeString(alert.availabilityHours));
            alerts.push(availabilityName);
            alertHours.push(hours);
        }

        const isUserOffDuty = this.lastHosStatusType ? this.getIsUserOffDuty(this.lastHosStatusType) : false;
        this.store.dispatch(DataActions.setAlertAvailability({ alertsAvailability: availabilityAlerts[userId], userId }));

        if (alerts.length > 0 && !isUserOffDuty && (!window.DRIVE_DEBUG || !window.DRIVE_DEBUG.DISABLE_AVAILABILITY_ALERTS)) {
            let userName = this.getUserFullName();
            let stringsForTranslation = [
                "Upcoming violation",
                `Upcoming violation${ (userName ? " for {0}" : "") }`,
                "{0} left",
                "h",
                "{minutes}m"
            ];

            stringsForTranslation.push.apply(stringsForTranslation, alerts);
            return this.translationService.getTranslator().translate(stringsForTranslation).then(alertStrings => {
                let audibleAlerts = [((alertStrings).shift() as string)];
                let violationTitle = ((alertStrings).shift() as string).replace("{0}", htmlEscape(userName));
                let remainingString = ((alertStrings).shift() as string);
                let hoursUnitString = ((alertStrings).shift() as string);
                let minutesUnitString = ((alertStrings).shift() as string).replace("{minutes}", "");
                let formattedLimits = this.replaceAllHoursInString(alertStrings, alertHours);
                let formatTimeString = (timeString: string) => timeString.replace("{0}", hoursUnitString).replace("{1}", minutesUnitString);
                let formattedAlerts = formattedLimits.map((alert, index) => `${ remainingString.replace("{0}", formatTimeString(alertDurations[index])) }: ${ alert }`);

                let language = this.translationService.getTranslator().language;
                audibleAlerts = alertStrings.length <= 2 ? audibleAlerts.concat(formattedLimits) : audibleAlerts;
                let audibleText = audibleAlerts.join(". ");

                if (isAppInBackground()) {
                    logger.log(logger.Source.DEBUG, `nativeSpeak: Triggered in background: "${ audibleText }}" (AppType: ${ AppType[getAppType()] })`);
                    nativeSpeak(audibleText, null, language);
                    let nativeNotifyMessage = formattedAlerts.join("\n");
                    getNotificationController().nativeNotify(nativeNotifyMessage, violationTitle)
                        .catch((error) => {
                            logger.error(logger.Source.NATIVE, `nativeNotify: Failed to notify upcoming violation in background: ${ error?.message || error }`, apiUser);
                        });
                } else {
                    if (this.store.getState().mobile.pageState.isLocked) {
                        logger.log(logger.Source.DEBUG, `nativeSpeak: Triggered speech for upcoming violation: "${ audibleText }" (AppType: ${ AppType[getAppType()] })`);
                        nativeSpeak(audibleAlerts.join(". "), null, language);
                    }
                    this.store.dispatch(MobileActions.setNewUpcomingViolation(true));
                    this.availabilityDialog = getAvailabilityDialog(this.store, formattedAlerts, violationTitle, this.translationService.getTranslator());
                    this.availabilityDialog.open(this.translationService.getTranslator()).then(() => {
                        logger.log(logger.Source.DEBUG, `Upcoming violation dialog was displayed to driver: "${ formattedAlerts.join("\n") }"`, apiUser);
                    });
                }
            });
        }
        return Promise.resolve();
    }

    private getIsUserOffDuty(status: string) {
        return status === "OFF" || status === "SB" || status === "PC" || status === "PC_Exempted";
    }

    private recreateRuleSetsDict(ruleSets: DeepReadonly<IHosRuleset []>): IIdDictionary<DeepReadonly<IHosRuleset>> {
        let ruleSetsDict: IIdDictionary<DeepReadonly<IHosRuleset>> = {};
        for (let i = 0; i < ruleSets.length; i++) {
            let id = ruleSets[i].id;
            if (id) {
                ruleSetsDict[id] = ruleSets[i];
            }
        }
        return ruleSetsDict;
    }

    private getLatestUserHosRuleSetMatching(predicate: (rulesetId: string) => boolean): UserHosRuleSet | undefined {
        let rulesets = this.getUserHosRuleSets();
        let matching: UserHosRuleSet[] = [];

        for (const key of rulesets.keys) {
            let userRuleSet = rulesets.get(key);
            if (predicate(userRuleSet.hosRuleSet.id)) {
                matching.push(userRuleSet);
            }
        }
        return getLatestRuleSet(matching);
    }
}
